<template>
  <Modal :show="state.show" @close="close" ref="modal" class="z-30">
    <div :class="width" class="m-4 mb-8 transform transition-all sm:w-full" ref="dialog">
      <div class="rounded overflow-hidden relative shadow-xl" tabindex="0">
        <header v-if="state.error" class="bg-red-500 px-6 py-4 text-white leading-snug text-sm">
          <p v-html="state.error"/>
        </header>

        <main class="bg-gray-100 p-6 border-b border-gray-100">
          <slot name="default" />
        </main>

        <footer class="bg-white px-6 py-4 space-x-4 flex justify-between">
          <Button
            v-if="cancel"
            icon="cancel"
            :text="cancel"
            @click.native="onCancel"
          />
          <Button
            v-if="button"
            class="ml-auto"
            :autofocus="autofocus"
            :class="color"
            :disabled="disabled"
            :form="form"
            :icon="icon"
            :text="button"
            @click.native="onSubmit"
          />
        </footer>
      </div>
    </div>
  </Modal>
</template>

<script>
import Button from 'Web/Shared/Button.vue';
import Icon from 'Web/Shared/Icon.vue';
import Modal from 'Web/Shared/Modal.vue';
import { computed, nextTick, reactive, ref, watch } from 'vue';
import rescue from '@/helpers/rescue';

export default {
  inheritAttrs: false,
  emits: [
    'cancel',
    'close',
    'open',
    'submit',
  ],
  components: {
    Button,
    Icon,
    Modal,
  },
  props: {
    autofocus: {
      type: Boolean,
      default: true,
    },
    button: {
      type: String,
      default: 'Ok',
    },
    cancel: {
      type: [String, Boolean],
      default: 'Abbrechen',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    form: {
      type: String,
      default: null,
    },
    icon: {
      type: String,
      default: 'check',
    },
    show: {
      type: Boolean,
      default: null,
    },
    size: {
      type: String,
      default: 'xl',
    },
    theme: {
      type: String,
      default: 'default',
    },
  },
  setup(props, { emit }) {
    const dialog = ref();
    const modal = ref();

    const state = reactive({
      active: null,
      dialog: null,
      error: null,
      modal: null,
      show: null,
    });

    function open() {
      state.active = document.activeElement;
      state.show = true;

      emit('open');

      nextTick(focus);
    }

    function focus() {
      setTimeout(() => {
        rescue(() => dialog.value.querySelector('[tabindex]').focus());
        rescue(() => dialog.value.querySelector('[autofocus]').focus());
      }, 10);
    }

    function close() {
      state.active.focus();
      state.active = null;
      state.show = false;
      emit('close');
    }

    function error(message) {
      state.error = message;
      nextTick(() => rescue(modal.value.element.scrollTo(0, 0)));
    }

    function clearError() {
      state.error = null;
    }

    function onCancel() {
      emit('cancel');
      close();
    }

    function onSubmit() {
      emit('submit');
    }

    watch(() => props.show, () => {
      if (props.show === null) return;
      nextTick(() => props.show ? open() : close());
    }, { immediate: true });

    const width = computed(() => ({
      'xs': 'sm:max-w-xs',
      'sm': 'sm:max-w-sm',
      'md': 'sm:max-w-md',
      'lg': 'sm:max-w-lg',
      'xl': 'sm:max-w-xl',
      '2xl': 'sm:max-w-2xl',
    }[props.size]));

    const color = computed(() => ({
      'default': 'text-blue-500',
      'positive': 'text-green-500',
      'negative': 'text-red-500',
    }[props.theme]));

    return {
      clearError,
      close,
      color,
      dialog,
      error,
      modal,
      onCancel,
      onSubmit,
      open,
      state,
      width,
    }
  }
}
</script>
